import React, { useState } from "react";
import clsx from "clsx";
import { createUseStyles, useTheme } from "react-jss";
import { isUndefined } from "engage-common/utils";
var ForegroundColor = "rgba(19, 19, 21, 0.6)";
var ForegroundHoverColor = "#121212";
var ForegroundFocusColor = "#1e4bd1";
var TextColor = "black";
var FilledBackgroundColor = "rgba(0, 0, 0, 0.09)";
var FilledBackgroundHoverColor = "rgba(0, 0, 0, 0.18)";
//Styles pulled from MaterialUI for now.
var useStyles = createUseStyles({
    inputGroup: {
        position: "relative",
        display: "inline-flex",
        flexDirection: "column",
        verticalAlign: "top",
        fontFamily: '"Anova", "Roboto", "Helvetica", "Arial", "sans-serif"',
        "& *": {
            fontFamily: "inherit"
        }
    },
    inputContainer: {
        position: "relative",
        boxSizing: "border-box",
        lineHeight: "1.1876em",
        letterSpacing: "0.00938em",
        cursor: "text",
        "&.standard": {
            marginTop: 16,
            "&:hover:before": {
                borderBottom: function (props) { return "2px solid ".concat(props.foregroundHoverColor); },
            },
        },
        "&.filled": {
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            transition: "background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
            backgroundColor: function (props) { return props.backgroundColor; },
            "&:hover": {
                backgroundColor: function (props) { return props.backgroundHoverColor; },
                "&:before": {
                    borderBottom: function (props) { return "1px solid ".concat(props.foregroundHoverColor); },
                }
            },
            "&.focused": {
                backgroundColor: function (props) { return props.backgroundFocusColor; },
            },
        },
        "&:before": {
            left: 0,
            right: 0,
            bottom: 0,
            content: '""',
            position: "absolute",
            transition: "border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
            borderBottom: function (props) { return "1px solid ".concat(props.foregroundColor); },
            pointerEvents: "none"
        },
        "&:after": {
            left: 0,
            right: 0,
            bottom: 0,
            content: '""',
            position: "absolute",
            transform: "scaleX(0)",
            transition: "transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
            pointerEvents: "none",
            borderBottom: function (props) { return "2px solid ".concat(props.foregroundFocusColor); },
        },
        "&.focused:after": {
            transform: "scaleX(1)",
        },
    },
    input: {
        boxSizing: "content-box",
        border: "none",
        width: "100%",
        fontSize: 16,
        fontWeight: 400,
        height: "1.1876em",
        background: "none",
        transition: "all 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
        color: function (props) { return props.textColor; },
        "&:focus": {
            outline: "none",
        },
        "&.standard": {
            padding: [6, 0, 7, 0],
        },
        "&.filled": {
            padding: [27, 12, 10],
            width: "calc(100% - 24px)",
        },
        /*"&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 1000px #1D1F42 inset",
            "-webkit-text-fill-color": "white"
        },*/
    },
    labelText: {
        position: "absolute",
        top: 0,
        left: 0,
        transformOrigin: "top left",
        color: function (props) { return props.foregroundColor; },
        transition: "color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
        pointerEvents: "none",
        zIndex: 1,
        "&.focused": {
            color: function (props) { return props.foregroundFocusColor; }
        },
        "&.standard": {
            transform: "translate(0, 24px) scale(1)",
            "&.shrink": {
                transform: "translate(0, 0) scale(0.75)",
            }
        },
        "&.filled": {
            transform: "translate(12px, 20px) scale(1)",
            "&.shrink": {
                transform: "translate(12px, 10px) scale(0.75)",
            }
        },
    },
    helper: {
        fontSize: "0.75rem",
        color: function (props) { return props.foregroundColor; },
        fontWeight: function (props) { return props.helperFontWeight; },
        letterSpacing: "0.0275rem",
        marginTop: 3,
        "&.filled": {
            padding: [0, 14],
        }
    }
});
var TextField = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    var error = !isUndefined(props.errorText);
    // @ts-ignore
    var theme = useTheme();
    var classes = useStyles({
        textColor: (_a = props.textColor) !== null && _a !== void 0 ? _a : TextColor,
        foregroundColor: error ? (_b = props.errorColor) !== null && _b !== void 0 ? _b : theme.color.error.main : (_c = props.foregroundColor) !== null && _c !== void 0 ? _c : ForegroundColor,
        foregroundHoverColor: error ? (_d = props.errorColor) !== null && _d !== void 0 ? _d : theme.color.error.main : (_e = props.foregroundHoverColor) !== null && _e !== void 0 ? _e : ForegroundHoverColor,
        foregroundFocusColor: error ? (_f = props.errorColor) !== null && _f !== void 0 ? _f : theme.color.error.main : (_g = props.foregroundFocusColor) !== null && _g !== void 0 ? _g : ForegroundFocusColor,
        backgroundColor: (_h = props.backgroundColor) !== null && _h !== void 0 ? _h : FilledBackgroundColor,
        backgroundHoverColor: (_j = props.backgroundHoverColor) !== null && _j !== void 0 ? _j : FilledBackgroundHoverColor,
        backgroundFocusColor: (_l = (_k = props.backgroundFocusColor) !== null && _k !== void 0 ? _k : props.backgroundColor) !== null && _l !== void 0 ? _l : FilledBackgroundColor,
        helperFontWeight: error ? 700 : 400,
    });
    var variant = (_m = props.variant) !== null && _m !== void 0 ? _m : "standard";
    var _s = useState(false), focused = _s[0], setFocused = _s[1];
    var shrinkLabel = "".concat((_o = props.value) !== null && _o !== void 0 ? _o : "").length || focused || ((_p = props.placeholder) === null || _p === void 0 ? void 0 : _p.length);
    var textLabelClass = clsx(classes.labelText, shrinkLabel && "shrink", focused && "focused", variant);
    var containerClass = clsx(classes.inputContainer, focused && "focused", variant);
    var inputClass = clsx(classes.input, props.inputClassName, variant);
    var helperClass = clsx(classes.helper, variant);
    function onBlur(e) {
        setFocused(false);
        if (!isUndefined(props.onBlur)) {
            props.onBlur(e);
        }
    }
    return (React.createElement("div", { className: clsx(props.className, classes.inputGroup), "data-qa": "textfield", "data-value": props.labelText },
        React.createElement("span", { className: textLabelClass },
            props.labelText,
            props.required ? " *" : ""),
        React.createElement("div", { className: containerClass },
            React.createElement("input", { name: props.name, required: props.required, value: (_q = props.value) !== null && _q !== void 0 ? _q : "", onChange: props.onChange, autoComplete: props.autoComplete, className: inputClass, autoFocus: props.autoFocus, onBlur: onBlur, onFocus: function () { return setFocused(true); }, type: props.type, disabled: props.disabled, placeholder: props.placeholder })),
        React.createElement("span", { className: helperClass }, (_r = props.errorText) !== null && _r !== void 0 ? _r : props.helperText)));
};
export default TextField;
//# sourceMappingURL=TextField.js.map